import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AppLayout } from './components/AppLayout.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { Landing } from './routes/Landing.js';
import { Login } from './routes/Login.js';
import { Register } from './routes/Register.js';
import { Dashboard } from './routes/Dashboard.js';

// Monaco is by far the largest dependency in the app. Loading it lazily keeps
// it out of the landing page and dashboard bundles entirely.
const Playground = lazy(() =>
  import('./routes/Playground.js').then((module) => ({ default: module.Playground })),
);
const Learn = lazy(() => import('./routes/Learn.js').then((module) => ({ default: module.Learn })));
const Lesson = lazy(() =>
  import('./routes/Lesson.js').then((module) => ({ default: module.Lesson })),
);
const Interview = lazy(() =>
  import('./routes/Interview.js').then((module) => ({ default: module.Interview })),
);
const InterviewSession = lazy(() =>
  import('./routes/InterviewSession.js').then((module) => ({ default: module.InterviewSession })),
);
const History = lazy(() =>
  import('./routes/History.js').then((module) => ({ default: module.History })),
);
const Groups = lazy(() =>
  import('./routes/Groups.js').then((module) => ({ default: module.Groups })),
);
const Admin = lazy(() => import('./routes/Admin.js').then((module) => ({ default: module.Admin })));

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" role="status" aria-live="polite">
      <Loader2 className="size-6 animate-spin text-slate-400" aria-hidden="true" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      {/* Auth pages own the full viewport, so they sit outside the app chrome. */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      <Route element={<AppLayout />}>
        <Route index element={<Landing />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route
            path="/groups"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Groups />
              </Suspense>
            }
          />
          <Route
            path="/playground"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Playground />
              </Suspense>
            }
          />
          <Route
            path="/learn"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Learn />
              </Suspense>
            }
          />
          <Route
            path="/learn/:slug"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Lesson />
              </Suspense>
            }
          />
          <Route
            path="/interview"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Interview />
              </Suspense>
            }
          />
          <Route
            path="/interview/:id"
            element={
              <Suspense fallback={<RouteFallback />}>
                <InterviewSession />
              </Suspense>
            }
          />
          <Route
            path="/history"
            element={
              <Suspense fallback={<RouteFallback />}>
                <History />
              </Suspense>
            }
          />
          {/* The admin role is enforced by the API; the route itself only
              redirects a learner who guesses the URL. */}
          <Route
            path="/admin"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Admin />
              </Suspense>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
